import { MdClose } from 'react-icons/md';
import { Portal } from '../../../components/Modal';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ISummerCampEnquiry, TSummerCampEnquiryStatus } from '@wot/typings';
import { format, parseISO } from 'date-fns';
import { Button, SelectDropDown } from '@wot/components';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import summerCampApi from '../../../api/summerCamp';
import Chip from '../../../components/Chip';

const STATUS_OPTIONS: TSummerCampEnquiryStatus[] = ['new', 'contacted', 'qualified', 'converted', 'closed'];

export default function SummerCampEnquiryDetail() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState<ISummerCampEnquiry | null>(null);
  const [status, setStatus] = useState<TSummerCampEnquiryStatus>('new');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { enquiryId } = useParams();

  useEffect(() => {
    if (enquiryId) {
      summerCampApi.getById(enquiryId).then((data) => {
        setEnquiry(data);
        setStatus(data.status);
      });
    }
  }, [enquiryId]);

  const handleClose = () => navigate(-1);

  const handleSave = async () => {
    if (!enquiry) return;
    setSaving(true);
    try {
      const updatedNotes = notes ? [...(enquiry.notes || []), notes] : enquiry.notes;
      await summerCampApi.update(enquiry._id, { status, notes: updatedNotes });
      toast.success('Enquiry updated');
      queryClient.invalidateQueries({ queryKey: ['summer-camp-enquiries'] });
      handleClose();
    } catch {
      toast.error('Failed to update enquiry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!enquiry) return;
    setDeleting(true);
    try {
      await summerCampApi.remove(enquiry._id);
      toast.success('Enquiry deleted');
      queryClient.invalidateQueries({ queryKey: ['summer-camp-enquiries'] });
      handleClose();
    } catch {
      toast.error('Failed to delete enquiry');
    } finally {
      setDeleting(false);
    }
  };

  if (!enquiry) return null;

  return (
    <Portal>
      <div className="relative w-full h-screen flex justify-center -z-10">
        <div onClick={handleClose} className="absolute top-0 left-0 w-full h-full bg-black/70 -z-10" />
        <div className="w-full h-full mx-auto my-auto flex justify-end">
          <div className="w-[40%] h-full flex flex-col gap-5 bg-white rounded-l-2xl p-7 overflow-y-scroll">
            <div className="w-full flex items-center justify-between">
              <h1 className="text-lg font-semibold">Enquiry {enquiry._id.slice(-8)}</h1>
              <MdClose className="text-2xl cursor-pointer" onClick={handleClose} />
            </div>

            <div className="flex flex-col gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span className="font-medium">{format(parseISO(enquiry.createdAt), 'd LLL, yyyy h:mm a')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Name</span>
                <span className="font-medium">{enquiry.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Email</span>
                <span className="font-medium">{enquiry.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Phone</span>
                <span className="font-medium">{enquiry.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Program</span>
                <span className="font-medium">Program {enquiry.program}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Source</span>
                <span className="font-medium">{enquiry.source}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">IP</span>
                <span className="font-medium">{enquiry.ip}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Email Sent</span>
                <Chip state={enquiry.emailSent ? 'success' : 'error'}>
                  <p className="text-sm font-semibold">{enquiry.emailSent ? 'Yes' : 'No'}</p>
                </Chip>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Admin Notified</span>
                <Chip state={enquiry.adminNotified ? 'success' : 'error'}>
                  <p className="text-sm font-semibold">{enquiry.adminNotified ? 'Yes' : 'No'}</p>
                </Chip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Status</span>
                <div className="w-40">
                  <SelectDropDown
                    value={status}
                    onChange={(val) => setStatus(val as TSummerCampEnquiryStatus)}
                    options={STATUS_OPTIONS}
                    placeholder="Select status"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 p-4 border border-primary rounded-md">
              <p className="text-sm font-medium uppercase text-primary">Message</p>
              <p className="text-sm">{enquiry.message}</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Notes ({enquiry.notes?.length || 0})</p>
              {enquiry.notes?.map((note, i) => (
                <p key={i} className="text-sm bg-gray-50 p-2 rounded">{note}</p>
              ))}
              <textarea
                className="w-full border border-gray-300 rounded-md p-2 text-sm mt-2"
                rows={3}
                placeholder="Add a note..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="w-full flex items-center gap-5 mt-auto">
              <Button type="s-outlined" block text="Cancel" onClick={handleClose} />
              <Button type="primary" block text="Delete" loading={deleting} onClick={handleDelete} className="!bg-red-600 !border-red-600 hover:!bg-red-700" />
              <Button block text="Save" loading={saving} onClick={handleSave} />
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
